import React, { useState, useMemo, useRef } from 'react';
import {
    Box, Grid, TextField, MenuItem, Typography, Paper, Avatar, Divider, Stack, useTheme
} from '@mui/material';
import {
    Badge as BadgeIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    School as SchoolIcon,
    ContactPhone as EmergencyIcon
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import GenericPage from '../../Commun/components/GenericPage';
import { academyStore } from '../services/academyStore';
import { Student } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';
import { exportToExcel, exportToWord } from '../utils/printExportUtils';

export const PrintStudentCardsPage: React.FC = () => {
    const { language, t } = useLanguage();
    const theme = useTheme();
    const { user } = useAuth();
    const { getParam } = useSystemParameters();
    const isRtl = language === 'ar';

    const students = academyStore.getStudents();
    const groups = academyStore.getGroups();
    const groupStudents = academyStore.getGroupStudents();

    const [selectedStudentId, setSelectedStudentId] = useState<number | 'all'>('all');
    const [selectedGroupId, setSelectedGroupId] = useState<number | 'all'>('all');

    const printableRef = useRef<HTMLDivElement>(null);

    const agencyName = user?.dosLib || user?.agency || getParam('DOS_LIB') || getParam('Agency_Name') || 'BLOBTIC ACADEMY';
    const agencyPhone = user?.dosTelph || getParam('DOS_TELPH') || getParam('Agency_Phone') || '';
    const agencyEmail = user?.dosEmail || getParam('DOS_EMAIL') || getParam('Agency_Email') || '';
    const agencyAddress = user?.dosAdr1 || getParam('DOS_ADR1') || getParam('Agency_Address') || '';
    const agencyLogo = user?.dosLogo;

    // Filter students
    const displayedStudents = useMemo(() => {
        let list = [...students];

        if (selectedGroupId !== 'all') {
            const enrolled = new Set(
                groupStudents
                    .filter(gs => gs.group_id === selectedGroupId && gs.status !== 'Dropped')
                    .map(gs => gs.student_id)
            );
            list = list.filter(s => enrolled.has(s.id));
        }

        if (selectedStudentId !== 'all') {
            list = list.filter(s => s.id === selectedStudentId);
        }

        return list.sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
    }, [students, selectedGroupId, selectedStudentId, groupStudents]);

    // Export to Excel
    const handleExportExcel = () => {
        const headers = [
            language === 'ar' ? 'الرقم الترتيبي' : language === 'en' ? 'Student ID' : 'N° Matricule',
            language === 'ar' ? 'اللقب والاسم' : language === 'en' ? 'Full Name' : 'Nom et prénom',
            language === 'ar' ? 'الرمز' : language === 'en' ? 'Code' : 'Code QR',
            language === 'ar' ? 'تاريخ الميلاد' : language === 'en' ? 'Date of Birth' : 'Date de naissance',
            language === 'ar' ? 'الهاتف' : language === 'en' ? 'Phone' : 'Téléphone'
        ];

        const rows = displayedStudents.map(s => [
            s.code || s.STD_CODE || s.id,
            `${s.last_name || ''} ${s.first_name || ''}`.trim(),
            `BLO-STD-${s.uid || s.STD_UIDF || s.id}`,
            s.date_of_birth || '—',
            s.phone || s.guardian_phone || '—'
        ]);

        exportToExcel('Badges_Apprenant', 'Badges', headers, rows);
    };

    // Export to Word
    const handleExportWord = () => {
        if (!printableRef.current) return;
        const title = language === 'ar' ? 'شارة التلميذ' : language === 'en' ? 'Student Badge' : "Badge d'apprenant";
        exportToWord('Badges_Apprenant', title, printableRef.current.innerHTML, isRtl);
    };

    // Render single student badge card conforming exactly to StudentCardModal design
    const renderStudentCard = (student: Student) => {
        const studentUid = student.uid || student.STD_UIDF || (student.id != null ? String(student.id) : '');
        const qrCodeValue = `BLO-STD-${studentUid}`;
        const studentCode = student.code || student.STD_CODE || `STD-${String(student.id).padStart(4, '0')}`;

        return (
            <Paper
                key={student.id}
                elevation={4}
                className="badge-card-item"
                sx={{
                    width: '320px',
                    minHeight: '480px',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    bgcolor: '#ffffff',
                    color: '#1e293b',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid',
                    mx: 'auto',
                    WebkitPrintColorAdjust: 'exact !important',
                    printColorAdjust: 'exact !important'
                }}
            >
                {/* Top Lanyard Slot Graphic */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    pt: 1,
                    pb: 0.5,
                    bgcolor: `${theme.palette.primary.main} !important`,
                    WebkitPrintColorAdjust: 'exact !important',
                    printColorAdjust: 'exact !important'
                }}>
                    <Box sx={{ width: 44, height: 6, borderRadius: 3, bgcolor: 'rgba(255, 255, 255, 0.4) !important' }} />
                </Box>

                {/* Header Banner with Agency Branding */}
                <Box
                    sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%) !important`,
                        color: '#ffffff !important',
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        WebkitPrintColorAdjust: 'exact !important',
                        printColorAdjust: 'exact !important'
                    }}
                >
                    {agencyLogo ? (
                        <Box
                            component="img"
                            src={agencyLogo}
                            alt="Logo"
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '8px',
                                bgcolor: '#ffffff',
                                p: 0.5,
                                objectFit: 'contain',
                                flexShrink: 0
                            }}
                        />
                    ) : (
                        <Avatar sx={{ width: 44, height: 44, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', flexShrink: 0 }}>
                            <SchoolIcon />
                        </Avatar>
                    )}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={800} noWrap sx={{ letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.85rem', color: '#ffffff !important' }}>
                            {agencyName}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.68rem', display: 'block', fontWeight: 700, color: 'rgba(255,255,255,0.9) !important' }} noWrap>
                            {t('academy.students.card_subtitle') || (language === 'ar' ? 'شارة التلميذ' : language === 'en' ? 'Student Badge' : "Badge d'apprenant")}
                        </Typography>
                    </Box>
                </Box>

                {/* Student Photo & Identity Section */}
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', flex: 1 }}>
                    <Avatar
                        className="badge-photo-avatar"
                        src={student.photo_base64}
                        sx={{
                            width: 84,
                            height: 84,
                            minWidth: 84,
                            minHeight: 84,
                            flexShrink: 0,
                            mb: 1.2,
                            border: `3px solid ${theme.palette.primary.main}`,
                            boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                            fontSize: '1.8rem',
                            fontWeight: 700,
                            bgcolor: theme.palette.primary.light,
                            color: theme.palette.primary.contrastText,
                            '& img': {
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }
                        }}
                    >
                        {!student.photo_base64 && `${student.first_name?.[0] || ''}${student.last_name?.[0] || ''}`}
                    </Avatar>

                    <Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a', lineHeight: 1.2, mb: 0.3 }}>
                        {student.first_name} {student.last_name}
                    </Typography>

                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', mb: 1.2 }}>
                        {studentCode}
                    </Typography>

                    {/* QR Code Container with High-Contrast White Background */}
                    <Box
                        sx={{
                            p: 1,
                            bgcolor: '#ffffff',
                            border: '2px dashed',
                            borderColor: theme.palette.primary.main,
                            borderRadius: '12px',
                            display: 'inline-flex',
                            boxShadow: 'inset 0 0 4px rgba(0,0,0,0.05)',
                            mb: 1.5
                        }}
                    >
                        <QRCodeSVG
                            value={qrCodeValue}
                            size={115}
                            level="H"
                            includeMargin={true}
                        />
                    </Box>

                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600, mb: 1 }}>
                        {qrCodeValue}
                    </Typography>

                    {/* Student metadata list */}
                    <Stack spacing={0.4} sx={{ width: '100%', px: 1, textAlign: isRtl ? 'right' : 'left', fontSize: '0.75rem' }}>
                        {student.date_of_birth && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" color="text.secondary">
                                    {language === 'ar' ? 'تاريخ الميلاد :' : language === 'en' ? 'Date of birth :' : 'Né(e) le :'}
                                </Typography>
                                <Typography variant="caption" fontWeight={600}>{student.date_of_birth}</Typography>
                            </Box>
                        )}
                        {student.emergency_contact && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                    <EmergencyIcon sx={{ fontSize: 13, color: 'error.main' }} /> {language === 'ar' ? 'طوارئ :' : language === 'en' ? 'Emergency :' : 'Urgence :'}
                                </Typography>
                                <Typography variant="caption" fontWeight={600} color="error.main">{student.emergency_contact}</Typography>
                            </Box>
                        )}
                    </Stack>
                </Box>

                <Divider sx={{ borderStyle: 'dashed' }} />

                {/* Agency Contact Footer */}
                <Box sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: '#f8fafc !important',
                    fontSize: '0.68rem',
                    color: '#64748b',
                    WebkitPrintColorAdjust: 'exact !important',
                    printColorAdjust: 'exact !important'
                }}>
                    {agencyAddress && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                            <LocationIcon sx={{ fontSize: 12, color: theme.palette.primary.main, flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.68rem' }} noWrap>{agencyAddress}</Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {agencyPhone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 12, color: theme.palette.primary.main, flexShrink: 0 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>{agencyPhone}</Typography>
                            </Box>
                        )}
                        {agencyEmail && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 12, color: theme.palette.primary.main, flexShrink: 0 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.68rem' }} noWrap>{agencyEmail}</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Paper>
        );
    };

    const filters = (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe'}
                    value={selectedGroupId}
                    onChange={e => setSelectedGroupId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                >
                    <MenuItem value="all">
                        {language === 'ar' ? 'جميع الأقسام' : language === 'en' ? 'All classes' : 'Toutes les classes'}
                    </MenuItem>
                    {groups.map(g => (
                        <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                    ))}
                </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'اختيار التلميذ' : language === 'en' ? 'Who' : 'Qui'}
                    value={selectedStudentId}
                    onChange={e => setSelectedStudentId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                >
                    <MenuItem value="all">
                        {language === 'ar' ? `— بطاقات كل القائمة (${displayedStudents.length}) —` : language === 'en' ? `— All students sheet (${displayedStudents.length}) —` : `— Planche de toute la liste (${displayedStudents.length}) —`}
                    </MenuItem>
                    {students.map(s => (
                        <MenuItem key={s.id} value={s.id}>
                            {s.last_name} {s.first_name} {s.code ? `(${s.code})` : ''}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
        </Grid>
    );

    return (
        <GenericPage
            title={language === 'ar' ? 'شارة التلميذ' : language === 'en' ? 'Student Badge' : "Badge d'apprenant"}
            icon={<BadgeIcon />}
            type="print"
            filters={filters}
            footerEnabled={false}
            onExportExcel={handleExportExcel}
            onExportWord={handleExportWord}
        >
            {/* Scoped CSS for high-quality badge printing */}
            <style>{`
                @media print {
                    @page {
                        size: A4 portrait;
                        margin: 10mm;
                    }
                    html, body {
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #fff !important;
                    }
                    * {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                        color-adjust: exact !important;
                    }
                    .badge-sheet-grid {
                        display: flex !important;
                        flex-wrap: wrap !important;
                        justify-content: center !important;
                        align-content: flex-start !important;
                        gap: 8mm !important;
                        width: 100% !important;
                        max-width: 190mm !important;
                        margin: 0 auto !important;
                        padding: 0 !important;
                    }
                    .badge-card-item {
                        width: 320px !important;
                        max-width: 320px !important;
                        min-height: 480px !important;
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                        box-shadow: none !important;
                        border: 1px dashed #94a3b8 !important;
                        margin: 0 !important;
                        border-radius: 16px !important;
                        overflow: hidden !important;
                        display: flex !important;
                        flex-direction: column !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                        box-sizing: border-box !important;
                    }
                    .badge-photo-avatar,
                    .badge-photo-avatar img,
                    .badge-photo-avatar .MuiAvatar-img {
                        width: 84px !important;
                        height: 84px !important;
                        min-width: 84px !important;
                        min-height: 84px !important;
                        max-width: 84px !important;
                        max-height: 84px !important;
                        flex-shrink: 0 !important;
                        border-radius: 50% !important;
                        object-fit: cover !important;
                    }
                }
            `}</style>
            <div ref={printableRef} dir={isRtl ? 'rtl' : 'ltr'}>
                {displayedStudents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                        {language === 'ar' ? 'لا يوجد تلاميذ' : language === 'en' ? 'No students found' : 'Aucun apprenant trouvé'}
                    </Typography>
                ) : (
                    <Box
                        className="badge-sheet-grid"
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            gap: 3,
                            py: 2,
                            maxWidth: 720,
                            mx: 'auto'
                        }}
                    >
                        {displayedStudents.map(s => renderStudentCard(s))}
                    </Box>
                )}
            </div>
        </GenericPage>
    );
};

export default PrintStudentCardsPage;
